import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Sidebar from "../components/Sidebar";
import { Star, ArrowLeft, MapPin, Calendar, Briefcase, MessageSquare, Sparkles } from "lucide-react";

export default function ReplacementProfile() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get(`/replacements/${id}`);
    setR(data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    setAi(null); setAiLoading(true);
    api.get(`/replacements/${id}/ai-summary`)
      .then(({ data }) => setAi(data))
      .catch(() => setAi(null))
      .finally(() => setAiLoading(false));
  }, [id]);

  if (!r) return <div className="flex"><Sidebar /><main className="flex-1 p-10 text-sm text-[#6B726A]">Chargement...</main></div>;

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <div className="max-w-4xl mx-auto p-6 lg:p-10">
          <button onClick={() => nav(-1)} className="text-sm text-[#6B726A] hover:text-[#1f2421] mb-6 flex items-center gap-2" data-testid="back-button">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>

          <div className="rp-card p-8">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-24 h-24 rounded-full bg-[#E8F0EA] text-[#1A4D2E] grid place-items-center font-medium text-3xl">
                {(r.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{r.name}</h1>
                <div className="text-sm text-[#6B726A] flex items-center gap-2 mt-1">
                  <Briefcase className="w-3.5 h-3.5" /> {r.professional_role || "Pharmacien"}
                </div>
                {r.rating_avg > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-sm">
                    <Star className="w-4 h-4 fill-[#E5A937] text-[#E5A937]" />
                    <span className="font-medium">{r.rating_avg.toFixed(1)}</span>
                    <span className="text-[#6B726A]">({r.reviews_count} avis)</span>
                  </div>
                )}
                <div className="flex gap-3 mt-4 flex-wrap text-xs text-[#6B726A]">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{r.experience_years || 0} ans d'expérience</span>
                  {r.address && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{r.address}</span>}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-[#D9CCFB] bg-[#F8F6FF] p-5" data-testid="ai-profile-summary">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-[#E8E5FF] grid place-items-center">
                  <Sparkles className="w-4 h-4 text-[#3F2EB2]" />
                </div>
                <span className="text-sm font-semibold text-[#3F2EB2]" style={{ fontFamily: "Outfit" }}>Analyse boostée par IA</span>
              </div>
              {aiLoading ? (
                <div className="text-sm text-[#6B726A]" data-testid="ai-summary-loading">Analyse du profil en cours…</div>
              ) : !ai ? (
                <div className="text-sm text-[#6B726A]" data-testid="ai-summary-error">Analyse IA indisponible pour le moment.</div>
              ) : (
                <div className="space-y-4">
                  {ai.summary && (
                    <div data-testid="ai-summary-text">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] mb-1">Résumé du profil</div>
                      <p className="text-sm text-[#1f2421] leading-relaxed">{ai.summary}</p>
                    </div>
                  )}
                  {ai.skills_highlights?.length > 0 && (
                    <div data-testid="ai-summary-skills">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] mb-1.5">Compétences clés</div>
                      <div className="flex flex-wrap gap-2">
                        {ai.skills_highlights.map((s) => (
                          <span key={s} className="text-xs px-3 py-1 rounded-full bg-white border border-[#D9CCFB] text-[#3F2EB2] font-medium">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {ai.experience_overview && (
                    <div data-testid="ai-summary-experience">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] mb-1">Expérience</div>
                      <p className="text-sm text-[#1f2421] leading-relaxed">{ai.experience_overview}</p>
                    </div>
                  )}
                  {ai.ai_opinion && (
                    <div className="rounded-xl bg-white/80 border border-[#D9CCFB] p-3.5" data-testid="ai-summary-opinion">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#3F2EB2] mb-1 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" /> Avis de l'IA
                      </div>
                      <p className="text-sm text-[#1f2421] leading-relaxed italic">{ai.ai_opinion}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={() => nav(`/messages?user=${id}`)}
              data-testid="contact-candidate-button"
              className="px-5 h-14 rounded-full border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA] flex items-center gap-2 text-sm font-medium">
              <MessageSquare className="w-4 h-4" /> Contacter
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
